import React, { useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import moment from 'moment';

import {
    useGetDocumentAuditsQuery,
    useGetDocumentsQuery,
    useGetReportingsQuery,
    useVerifySubscriptionMutation,
} from 'api/main';
import {
    billStatus as reportingStatus,
    subscriptionStatus,
    docTypes,
    paymentMethods,
    REPLACEABLE_INVESTOR_RELATED_DOCUMENTS,
    RoleNames,
    investorClassificationKeys,
    INVESTOR_RELATED_DOCUMENTS,
    INVESTOR_DOCUMENTS_TO_ARCHIVE,
    roleCategories,
} from 'utils/constants';
import {
    handleDocumentPreview,
    getDocumentJumioStatus,
    jsonParseSafe,
    ucFirst,
    parseFullName,
} from 'utils/helpers';
import {
    useMatchAtLeastOneRole,
    useSubscriptionCanBeVerifiedByDistributor,
    useSubscriptionFormSignatureStatus,
    useUserRoleMatchCategory,
} from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import InfoBox from 'containers/distributors/distributor-details/InfoBox';
import Document from '../Document';
import UploadDocument from '../UploadDocument';
import { ActionButton, Loader } from 'components';
import { Box, Typography } from '@mui/material';

const subtitleSx = {
    fontSize: '20px',
    marginBottom: '20px',
    fontWeight: '500',
};

const DocumentsSection = ({ subscription, user, getData }) => {
    const { userId } = useParams();

    const { data: reportings = [], isLoading: reportingsLoader } = useGetReportingsQuery(
        {
            pagination: false,
            subscriptionId: subscription?.id,
        },
        { skip: !subscription?.id }
    );

    const userIsStandardServicer = useMatchAtLeastOneRole([RoleNames.SERVICER_STANDARD]);
    const userIsNetworkAdmin = useMatchAtLeastOneRole([RoleNames.NETWORK_ADMIN]);
    const isAdminOrSignatory = useMatchAtLeastOneRole([
        RoleNames.SERVICER_SIGNATORY,
        RoleNames.SERVICER_ADMIN,
    ]);
    const userIsProvider = useUserRoleMatchCategory(roleCategories.PROVIDERS);
    const userIsPromoter = useUserRoleMatchCategory(roleCategories.PROMOTERS);
    const userIsServicer = useUserRoleMatchCategory(roleCategories.SERVICERS);
    const userIsAdminOrSignatoryServicer = useMatchAtLeastOneRole([
        RoleNames.SERVICER_ADMIN,
        RoleNames.SERVICER_SIGNATORY,
    ]);

    const {
        data: documents = [],
        isFetching: documentsLoader,
        refetch,
    } = useGetDocumentsQuery(
        { belongsTo: userId, userTypes: true, pagination: false },
        { skip: !userId }
    );

    const { data: documentAudits = [], isFetching: documentAuditsLoader } =
        useGetDocumentAuditsQuery(
            { belongsToId: userId, pagination: false },
            { skip: !userId || !userIsServicer }
        );
    const { data: coSubDocumentAudits = [], isFetching: coSubDocumentAuditsLoader } =
        useGetDocumentAuditsQuery(
            { belongsToId: subscription.coSubscriber?.id, pagination: false },
            { skip: !subscription.coSubscriber?.id || !userIsServicer }
        );
    const {
        data: coSubscriberDocuments = [],
        isFetching: coSubscriberDocumentsLoader,
        refetch: refetchCoSubscriber,
    } = useGetDocumentsQuery(
        { belongsTo: subscription.coSubscriber?.id, userTypes: true, pagination: false },
        { skip: !subscription.coSubscriber?.id }
    );

    const [downloadingDoc, setDownloadingDoc] = useState(null);

    const subscriptionCanBeVerified = useSubscriptionCanBeVerifiedByDistributor(subscription);

    const hasSignedBS = useSubscriptionFormSignatureStatus(subscription);
    const usesCreditTransfer = useMemo(
        () => subscription?.bankingStatement?.paymentMethod === paymentMethods.CREDIT_TRANSFER,
        [subscription]
    );
    const canUploadDocument = useMemo(
        () => !userIsNetworkAdmin && !userIsProvider && !userIsStandardServicer && !userIsPromoter,
        [userIsNetworkAdmin, userIsPromoter, userIsProvider, userIsStandardServicer]
    );
    const [
        verifySubscription,
        { isLoading: verifyLoader, isError: isVerifyError, error: verifyError },
    ] = useVerifySubscriptionMutation();
    const subscriptionVerifyError = isVerifyError && verifyError === 403;
    const isSubscriptionFinished = useMemo(
        () => subscription?.subscriptionStatus === subscriptionStatus.FINISHED,
        [subscription]
    );
    const investorClassificationDoc = useMemo(
        () =>
            user?.investorClassification === investorClassificationKeys.PRO
                ? documents.filter(doc => doc.type === docTypes.INVESTOR_CLASSIFICATION).pop() // use latest
                : null,
        [documents, user]
    );

    // documents by type
    const capitalCallDocuments = useMemo(
        () =>
            documents
                .filter(doc => doc.type === docTypes.CAPITAL_CALL || doc.type === docTypes.CLOSING)
                .map(doc => ({ ...doc, subtype: jsonParseSafe(doc.subtype) })),
        [documents]
    );
    const capitalAccountDocuments = useMemo(
        () =>
            documents
                .filter(doc => doc.type === docTypes.CAPITAL_ACCOUNT)
                .map(doc => ({ ...doc, subtype: jsonParseSafe(doc.subtype) })),
        [documents]
    );

    const eligibleReports = useMemo(
        () =>
            reportings.filter(
                r =>
                    (r.status === reportingStatus.ACTIVATED ||
                        r.status === reportingStatus.COMPLETED) &&
                    r.recipientType === 'subscribers'
            ),
        [reportings]
    );

    const filteredReports = useMemo(
        () => eligibleReports.filter(r => r.investmentFund.id === +subscription?.investmentFund.id),
        [eligibleReports, subscription]
    );

    const handlePreview = useCallback(doc => {
        handleDocumentPreview(doc, setDownloadingDoc);
    }, []);

    const handleDocumentsChange = useCallback(
        callback => {
            refetch();
            refetchCoSubscriber();
            callback();
        },
        [refetch, refetchCoSubscriber]
    );

    const filteredSubscriptionDocuments = useMemo(
        () =>
            Object.values(subscription?.subscriptionDocuments || []).filter(sd => {
                if (sd.type === docTypes.TAX_EXEMPTION) {
                    return !!user?.investorProfile?.taxExemption;
                }
                return true;
            }),
        [subscription, user]
    );

    const confidentialDocuments = useMemo(() => documents.filter(d => d.confidential), [documents]);

    const investorRelatedDocs = useMemo(() => {
        let docList = [];
        INVESTOR_RELATED_DOCUMENTS.forEach(key => {
            const otherDocuments = documents.filter(doc => doc.type === key && !doc.confidential);
            docList = docList.concat(otherDocuments);
        });
        docList = docList.concat(investorClassificationDoc ? [investorClassificationDoc] : []);
        return docList;
    }, [documents, investorClassificationDoc]);

    const handleVerifySubscription = useCallback(() => {
        verifySubscription(subscription.id);
    }, [subscription, verifySubscription]);

    return (
        <InfoBox title={texts.DOCUMENTS} disableLeftColumn>
            <Box px={4}>
                <Loader
                    loading={
                        documentsLoader ||
                        coSubscriberDocumentsLoader ||
                        documentAuditsLoader ||
                        coSubDocumentAuditsLoader
                    }
                >
                    {!!capitalCallDocuments.length && (
                        <Box mb={3}>
                            <Typography sx={subtitleSx}>{texts.FUND_CALLS}</Typography>
                            {capitalCallDocuments
                                .filter(
                                    doc =>
                                        doc.subtype?.investmentFund &&
                                        doc.subtype?.investmentFund ===
                                            subscription?.investmentFund.id
                                )
                                .map(doc => (
                                    <Document key={doc.id} doc={doc} />
                                ))}
                        </Box>
                    )}
                    {!!capitalAccountDocuments.length && (
                        <Box mb={3}>
                            <Typography sx={subtitleSx}>{texts.CAPITAL_ACCOUNTS}</Typography>
                            {capitalAccountDocuments
                                .filter(
                                    doc =>
                                        doc.subtype?.investmentFund &&
                                        doc.subtype?.investmentFund ===
                                            subscription?.investmentFund.id
                                )
                                .map(doc => (
                                    <Document key={doc.id} doc={doc} />
                                ))}
                        </Box>
                    )}
                    <Loader loading={reportingsLoader} forceStop={!reportingsLoader}>
                        {!!filteredReports.length && (
                            <Box mb={3} display="flex" flexDirection="column">
                                <Typography sx={subtitleSx}>{texts.MANAGEMENT_REPORTS}</Typography>
                                {filteredReports.map(r => (
                                    <Typography
                                        key={r.id}
                                        sx={{
                                            marginBottom: '15px',
                                            cursor: 'pointer',
                                            '&:hover': { textDecoration: 'underline' },
                                        }}
                                        onClick={() => handlePreview(r.document)}
                                        component="span"
                                    >
                                        <Loader
                                            loading={downloadingDoc === r.document.id}
                                            size={20}
                                            marginTop={0}
                                        >
                                            {`${moment(r.date).format('L')} - ${r.emailSubject}`}
                                        </Loader>
                                    </Typography>
                                ))}
                            </Box>
                        )}
                    </Loader>
                    {!!filteredSubscriptionDocuments.length && (
                        <Box mb={3}>
                            <Typography sx={subtitleSx}>
                                {ucFirst(texts.INVESTMENT_RELATED_DOCUMENTS)}
                            </Typography>
                            {filteredSubscriptionDocuments.map(doc => {
                                if (
                                    (doc.type === docTypes.SUBSCRIPTION_FORM ||
                                        doc.type === docTypes.SUBSCRIPTION_FORM_DEPRECATED) &&
                                    !hasSignedBS
                                ) {
                                    return null;
                                }
                                const jumioStatus = getDocumentJumioStatus(
                                    subscription?.userTunnelSteps,
                                    doc.type
                                );
                                const docIsReplaceable =
                                    !isSubscriptionFinished &&
                                    userIsAdminOrSignatoryServicer &&
                                    REPLACEABLE_INVESTOR_RELATED_DOCUMENTS.includes(doc.type);

                                return (
                                    <Document
                                        key={doc.id}
                                        doc={doc}
                                        isReplaceable={docIsReplaceable}
                                        onChange={handleDocumentsChange}
                                        jumioStatus={jumioStatus}
                                        withRIBValidation={
                                            isAdminOrSignatory &&
                                            doc.type === docTypes.RIB &&
                                            !usesCreditTransfer
                                        }
                                        subscription={subscription}
                                        onFinishReplace={() => {
                                            refetch();
                                            getData();
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    )}
                    {(!!investorRelatedDocs.length || !!coSubscriberDocuments.length) && (
                        <Box mb={3}>
                            <Typography sx={subtitleSx}>
                                {texts.INVESTOR_PROFILE_DOCUMENTS}
                            </Typography>
                            {investorRelatedDocs.map(doc => {
                                const jumioStatus = getDocumentJumioStatus(
                                    subscription?.userTunnelSteps,
                                    doc.type
                                );
                                const docIsReplaceable =
                                    !isSubscriptionFinished &&
                                    userIsAdminOrSignatoryServicer &&
                                    REPLACEABLE_INVESTOR_RELATED_DOCUMENTS.includes(doc.type);

                                const canArchive =
                                    userIsAdminOrSignatoryServicer &&
                                    (INVESTOR_DOCUMENTS_TO_ARCHIVE.includes(doc.type) ||
                                        INVESTOR_DOCUMENTS_TO_ARCHIVE.includes(doc.subtype)) &&
                                    isSubscriptionFinished;

                                return (
                                    <Document
                                        key={doc.id}
                                        doc={doc}
                                        isReplaceable={docIsReplaceable}
                                        onChange={handleDocumentsChange}
                                        jumioStatus={jumioStatus}
                                        useJumioIframe={isSubscriptionFinished}
                                        canArchive={canArchive}
                                        onFinishReplace={() => {
                                            refetch();
                                            getData();
                                        }}
                                    />
                                );
                            })}
                            {!!coSubscriberDocuments.length && (
                                <React.Fragment>
                                    {coSubscriberDocuments.map(doc => {
                                        const jumioStatus = getDocumentJumioStatus(
                                            subscription?.userTunnelSteps,
                                            doc.type
                                        );
                                        const docIsReplaceable =
                                            userIsAdminOrSignatoryServicer &&
                                            REPLACEABLE_INVESTOR_RELATED_DOCUMENTS.includes(
                                                doc.type
                                            );

                                        const canArchive =
                                            userIsAdminOrSignatoryServicer &&
                                            (INVESTOR_DOCUMENTS_TO_ARCHIVE.includes(doc.type) ||
                                                INVESTOR_DOCUMENTS_TO_ARCHIVE.includes(
                                                    doc.subtype
                                                )) &&
                                            isSubscriptionFinished;

                                        return (
                                            <Document
                                                key={doc.id}
                                                doc={doc}
                                                belongsToCoSubscriber
                                                canArchive={canArchive}
                                                isReplaceable={docIsReplaceable}
                                                onChange={handleDocumentsChange}
                                                jumioStatus={jumioStatus}
                                                onFinishReplace={() => {
                                                    refetch();
                                                    getData();
                                                }}
                                            />
                                        );
                                    })}
                                </React.Fragment>
                            )}
                        </Box>
                    )}
                    {!!confidentialDocuments.length && (
                        <Box mb={3}>
                            <Typography sx={subtitleSx}>{texts.CONFIDENTIAL_DOCUMENTS}</Typography>
                            {confidentialDocuments.map(doc => (
                                <Document key={doc.id} doc={doc} />
                            ))}
                        </Box>
                    )}
                    {!documents.length &&
                        !filteredReports.length &&
                        !filteredSubscriptionDocuments.length && (
                            <Typography align="center">
                                {texts.NO_SUBSCRIPTION_DOCUMENTS}
                            </Typography>
                        )}
                    {(!!documentAudits.length || !!coSubDocumentAudits.length) && (
                        <Box mb={3}>
                            <Typography sx={subtitleSx}>{texts.OLD_DOCUMENTS}</Typography>
                            {documentAudits.map(doc => {
                                return (
                                    <Document
                                        key={doc.id}
                                        doc={doc}
                                        isReplaceable={false}
                                        isAudit
                                    />
                                );
                            })}
                            {coSubDocumentAudits.map(doc => {
                                return (
                                    <Document
                                        key={doc.id}
                                        doc={doc}
                                        belongsToCoSubscriber
                                        isReplaceable={false}
                                        isAudit
                                    />
                                );
                            })}
                        </Box>
                    )}

                    {canUploadDocument && (
                        <UploadDocument
                            userId={user?.id}
                            hideSelect={userIsServicer}
                            displayConfidential={userIsServicer}
                            uploadByAdmin
                            sx={{ alignItems: 'center' }}
                        />
                    )}

                    {subscriptionCanBeVerified && (
                        <Box display="flex" alignItems={'center'} gap={3} mt={2}>
                            <Typography>{texts.DISTRIBUTOR_VERIFICATION}</Typography>
                            <ActionButton
                                variant="contained"
                                loading={verifyLoader}
                                onClick={handleVerifySubscription}
                            >
                                {texts.READY_TO_BE_VERIFIED}
                            </ActionButton>
                        </Box>
                    )}
                    {subscriptionVerifyError && subscription.validatedByCIF && (
                        <Typography color="error.main" mt={2}>
                            {texts.VERIFICATION_ERROR_TEXT(
                                parseFullName(subscription.validatedByCIF)
                            )}
                        </Typography>
                    )}
                    {subscription.validatedByCIF && !subscriptionVerifyError && (
                        <Typography mt={2}>
                            {texts.VERIFICATION_TEXT(
                                parseFullName(subscription.validatedByCIF),
                                moment(subscription.validatedByCIFAt).format('DD/MM/YYYY HH:mm')
                            )}
                        </Typography>
                    )}
                </Loader>
            </Box>
        </InfoBox>
    );
};

DocumentsSection.propTypes = {
    subscription: PropTypes.object,
    user: PropTypes.object,
    getData: PropTypes.func,
};

export default React.memo(DocumentsSection);
